import { get } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { z } from "zod";

const workerExternalIntegrationConfigurationSchema = z.object({
  type: z.literal("worker-external-integration-configuration"),
  id: z.string(),
  attributes: z.object({
    externalIntegrationType: z.string(),
    workerId: z.string(),
    tenantId: z.string(),
    externalId: z.string(),
  }),
});

export type WorkerExternalIntegrationConfiguration = z.infer<
  typeof workerExternalIntegrationConfigurationSchema
>;

const workerExternalIntegrationConfigurationResponseSchema = z.object({
  data: workerExternalIntegrationConfigurationSchema,
});

export type WorkerExternalIntegrationConfigurationResponse = z.infer<
  typeof workerExternalIntegrationConfigurationResponseSchema
>;

export function getWorkerExternalIntegrationConfigurationUrl(): string {
  return `${environmentConfig.REACT_APP_BASE_API_URL}/worker-external-integration-configurations`;
}

interface UseGetWorkerExternalIntegrationConfigurationParams {
  workerId: string;
  workplaceId: string;
}

export function useGetWorkerExternalIntegrationConfiguration(
  params: UseGetWorkerExternalIntegrationConfigurationParams
): UseQueryResult<WorkerExternalIntegrationConfiguration, AxiosError> {
  const { workerId, workplaceId } = params;

  return useQuery({
    queryKey: [getWorkerExternalIntegrationConfigurationUrl(), workerId, workplaceId],
    queryFn: async () => {
      const response = await get({
        url: getWorkerExternalIntegrationConfigurationUrl(),
        queryParams: {
          workerId,
          workplaceId,
        },
        responseSchema: workerExternalIntegrationConfigurationResponseSchema,
      });

      return response.data.data;
    },
    retry: false,
  });
}
